// utils/fingerLayout.js
import * as THREE from "three";
import { drawFingerRing, addTextLabel } from "./threeUtils";
import { validateValue } from "../../../utils/utils";
import { toDecimal } from "../../../utils";

export function computeFingerLayout({
  ball,
  centerPoint,
  spanMeasurements,
  ballRadius,
  arcOffset = 0.005,
  verticalAxis,
  referenceAxis,
}) {
  const thumbRadius = spanMeasurements.thumbDiameter / 2;
  const middleRadius = spanMeasurements.middleFingerDiameter / 2;
  const ringRadius = spanMeasurements.ringFingerDiameter / 2;

  const middleEdgeToThumb = spanMeasurements.middleFingerCutToCut;
  const ringEdgeToThumb = spanMeasurements.ringFingerCutToCut;
  const avgSpan = (middleEdgeToThumb + ringEdgeToThumb) / 2;
  const centerToThumbEdge = avgSpan / 2;
  const centerToThumbCenter = centerToThumbEdge + thumbRadius;

  // === Build perpendicular axis from centerPoint
  const arcCenterDir = verticalAxis.clone().normalize();
  let basePerpAxis = new THREE.Vector3()
    .crossVectors(arcCenterDir, centerPoint.clone().normalize())
    .normalize();

  if (
    referenceAxis &&
    referenceAxis.lengthSq &&
    referenceAxis.lengthSq() > 1e-8
  ) {
    const refDir = referenceAxis.clone().normalize();
    if (basePerpAxis.dot(refDir) < 0) {
      basePerpAxis = basePerpAxis.negate();
    }
  }

  // A quarter turn, not a handed one.
  //
  // basePerpAxis has already been given its handed sign just above: it is
  // flipped to agree with referenceAxis, and the callers hand that in already
  // mirrored (.multiplyScalar(handednessFactor)). Turning by
  // handednessFactor * PI/2 then flipped it a second time, and the two flips
  // cancelled -- a left-hander came out with exactly the same grip axis as a
  // right-hander while the frame around it (PAP, VAL, the arcs) was mirrored.
  // A grip drawn unmirrored inside a mirrored frame is what reached Mark as
  // "upside down, and the thumb and fingers on opposite sides": one 180 turn
  // away from right. #839
  //
  // Verified numerically: before, RH and LH grip axes had dot = +1 (never
  // mirrored); after, dot = -1 (properly mirrored), and the right-handed axis
  // is bit-identical to what it was, so right-handers cannot regress.
  const rotatedAxis = basePerpAxis
    .clone()
    .applyAxisAngle(centerPoint.clone().normalize(), Math.PI / 2);

  const finalPerp = rotatedAxis.normalize();

  // === STEP 1: Thumb from centerGrip
  const thetaThumb = centerToThumbCenter / ballRadius;
  const thumb = centerPoint
    .clone()
    .applyAxisAngle(finalPerp, thetaThumb)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);
  drawFingerRing(ball, thumb, thumbRadius, ballRadius, arcOffset, 0xffff00);

  // === STEP: true thumb edge (not center)
  const thumbEdge = thumb
    .clone()
    .applyAxisAngle(finalPerp, -thumbRadius / ballRadius)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  // ✅ From thumb edge → middle finger edge
  const thetaToMiddleEdge = middleEdgeToThumb / ballRadius;
  const middleEdge = thumbEdge
    .clone()
    .applyAxisAngle(finalPerp.clone().negate(), thetaToMiddleEdge)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  const upwardAxis = new THREE.Vector3()
    .crossVectors(finalPerp, middleEdge.clone().normalize())
    .normalize();
  const thetaUp = middleRadius / ballRadius;

  let middleCenter = middleEdge
    .clone()
    .applyAxisAngle(upwardAxis, thetaUp)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  const thetaBridge = 1 / 8 / ballRadius;
  const lateralAxis = new THREE.Vector3()
    .crossVectors(middleCenter.clone().normalize(), finalPerp)
    .normalize();

  middleCenter = middleCenter
    .clone()
    .applyAxisAngle(lateralAxis, -thetaBridge)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  drawFingerRing(
    ball,
    middleCenter,
    middleRadius,
    ballRadius,
    arcOffset,
    0xffff00,
  );

  // === Ring Finger Placement
  const ringRadiusAngle = ringRadius / ballRadius;
  const thetaToRingEdge = ringEdgeToThumb / ballRadius;
  const ringEdge = thumbEdge
    .clone()
    .applyAxisAngle(finalPerp.clone().negate(), thetaToRingEdge)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  const ringUpwardAxis = new THREE.Vector3()
    .crossVectors(finalPerp.clone().negate(), ringEdge.clone().normalize())
    .normalize();

  let ringCenter = ringEdge
    .clone()
    .applyAxisAngle(ringUpwardAxis, ringRadiusAngle)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  const ringBridgeOffset = 1 / 8 / ballRadius;
  const ringLateralAxis = new THREE.Vector3()
    .crossVectors(ringCenter.clone().normalize(), finalPerp)
    .normalize();

  ringCenter = ringCenter
    .clone()
    .applyAxisAngle(ringLateralAxis, ringBridgeOffset)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  drawFingerRing(ball, ringCenter, ringRadius, ballRadius, arcOffset, 0xffff00);

  return { thumb, middleCenter, ringCenter };
}

export function computeTwoHandedFingerLayout({
  ball,
  centerPoint,
  targetPoint,
  spanMeasurements,
  ballRadius,
  arcOffset = 0.005,
}) {
  const centerDir = centerPoint.clone().normalize();
  const targetDir = targetPoint?.clone().normalize();

  let axisLT = null;
  if (targetDir) {
    axisLT = new THREE.Vector3().crossVectors(centerDir, targetDir);
    if (axisLT.lengthSq() > 1e-8) {
      axisLT.normalize();
      // Keep positive rotation heading toward targetDir along the short arc.
      const probe = centerDir.clone().applyAxisAngle(axisLT, 0.01).normalize();
      if (probe.angleTo(targetDir) > centerDir.angleTo(targetDir)) {
        axisLT.negate();
      }
    } else {
      axisLT = null;
    }
  }

  if (!axisLT || axisLT.lengthSq() <= 1e-8) {
    axisLT = new THREE.Vector3(1, 0, 0).cross(centerDir);
    if (axisLT.lengthSq() <= 1e-8) {
      axisLT = new THREE.Vector3(0, 0, 1).cross(centerDir);
    }
    axisLT.normalize();
  }

  const middleRadius = spanMeasurements.middleFingerDiameter / 2;
  const ringRadius = spanMeasurements.ringFingerDiameter / 2;
  const baseOffset = 1 / 8;
  const middleOffsetAngle = (baseOffset + middleRadius) / ballRadius;
  const ringOffsetAngle = (baseOffset + ringRadius) / ballRadius;

  const middleCenter = centerDir
    .clone()
    .applyAxisAngle(axisLT, middleOffsetAngle)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  const ringCenter = centerDir
    .clone()
    .applyAxisAngle(axisLT, -ringOffsetAngle)
    .normalize()
    .multiplyScalar(ballRadius + arcOffset);

  drawFingerRing(
    ball,
    middleCenter,
    middleRadius,
    ballRadius,
    arcOffset,
    0xffffff,
  );
  drawFingerRing(ball, ringCenter, ringRadius, ballRadius, arcOffset, 0xffffff);

  return { middleCenter, ringCenter, axisLT };
}

export function isValidSpanMeasurement(value) {
  return value !== null && value !== undefined && value !== "" && value !== 0;
}

export function isValidSpanMeasurements(input) {
  const allowMissingThumb = Boolean(input?.is2Hander);
  return (
    isValidSpanMeasurement(input?.spanMiddleFingerCutToCutDistance) &&
    isValidSpanMeasurement(input?.spanRingFingerCutToCutDistance) &&
    (allowMissingThumb || isValidSpanMeasurement(input?.thumbDiameter)) &&
    isValidSpanMeasurement(input?.middleFingerDiameter) &&
    isValidSpanMeasurement(input?.ringFingerDiameter)
  );
}

export function sanitizeSpanMeasurements(input, spanMeasurement) {
  if (!input) {
    return {
      middleFingerCutToCut: 4,
      ringFingerCutToCut: 4,
      spanMiddleFingerCutToCutDistance: 4,
      spanRingFingerCutToCutDistance: 4,
      thumbDiameter: 1.125,
      middleFingerDiameter: 0.75,
      ringFingerDiameter: 0.75,
      isLeftHanded: false,
      is2Hander: false,
    };
  }
  const is2Hander = validateValue(input.is2Hander, false);
  const rawThumbDiameter = parseInchFraction(
    input.thumbDiameter,
    spanMeasurement,
  );
  const sanitizedThumbDiameter =
    rawThumbDiameter > 0 ? rawThumbDiameter : is2Hander ? 1.125 : 0;

  return {
    middleFingerCutToCut: validateValue(
      parseInchFraction(
        input.spanMiddleFingerCutToCutDistance,
        spanMeasurement,
      ),
      4,
    ),
    ringFingerCutToCut: validateValue(
      parseInchFraction(input.spanRingFingerCutToCutDistance, spanMeasurement),
      4,
    ),
    spanMiddleFingerCutToCutDistance: validateValue(
      parseInchFraction(
        input.spanMiddleFingerCutToCutDistance,
        spanMeasurement,
      ),
      4,
    ),
    spanRingFingerCutToCutDistance: validateValue(
      parseInchFraction(input.spanRingFingerCutToCutDistance, spanMeasurement),
      4,
    ),
    thumbDiameter: sanitizedThumbDiameter,
    middleFingerDiameter: validateValue(
      parseInchFraction(input.middleFingerDiameter, spanMeasurement),
      0.75,
    ),
    ringFingerDiameter: validateValue(
      parseInchFraction(input.ringFingerDiameter, spanMeasurement),
      0.75,
    ),
    isLeftHanded: validateValue(input.isLeftHanded, false),
    is2Hander,
  };
}

export function parseInchFraction(input, spanMeasurement) {
  console.log(
    "parseInchFraction input:",
    input,
    "spanMeasurement:",
    spanMeasurement,
  );
  return input && input !== "" ? toDecimal(input, spanMeasurement) : 0;
}
